<template>
  <FilterSortingSkeleton :loading="categoryStore.isExperiencesLoading">
    <div class="sorting">
      <div
        class="sorting__title"
        @click="togglePopoverHandler"
      >
        <IconBase
          name="sorting-icon"
          :size="20"
        />
        <span>{{ title }}</span>
      </div>

      <UiModal
        v-if="isMobile"
        :is-shown="isOpenPopover"
        modal-class="sorting-modal"
        :teleport-target="modalTarget"
        is-footer-hidden
        :title="t('sorting.title')"
        @close="closePopover"
      >
        <template #content>
          <div class="sorting-modal-content">
            <RadioSortingList
              :selected-value="categoryStore.currentSorting"
              @choose-sorting-value="chooseSortingValue"
            />
          </div>
        </template>
      </UiModal>

      <UiPopover
        v-else
        :open="isOpenPopover"
        @close="closePopover"
      >
        <template #content>
          <div class="sorting__list">
            <div
              v-for="item of listSort"
              :key="item.value"
              class="sorting-desktop__list-item"
              @click="chooseSortingValue(item.value)"
            >
              <span>
                {{ item.label }}
              </span>
              <IconBase
                v-if="isChosenSortValue(item.value)"
                name="check-mark"
                :size="20"
              />
            </div>
          </div>
        </template>
      </UiPopover>
    </div>
  </FilterSortingSkeleton>
</template>

<script setup lang="ts">
  import { useI18n } from '@experience/i18n';
  import { UiPopover } from '@experience/core-popover-ui';
  import { UiModal } from '@experience/core-modal-ui';
  import RadioSortingList from './RadioSortingList.vue';
  import { computed, ref } from 'vue';

  import { applyModalStyles, resetModalStyles } from '@/shared/lib';
  import { IconBase } from '@/shared/ui';
  import { useModalTarget } from '@/shared/utils/modalTarget';
  import { useScreenSize } from '@experience/shared';

  import ru from '@/features/sorting/i18n/ru';

  import FilterSortingSkeleton from './FilterSortingSkeleton.vue';
  import { useCategoryStore } from '@/shared/store';
  import type { ListSortItemValue } from '@/shared/models';
  import { useSortList } from '../composables';

  const categoryStore = useCategoryStore();

  const { t } = useI18n({
    inheritLocale: true,
    messages: { ru },
  });

  const { isMobile } = useScreenSize();

  const modalTarget = useModalTarget();

  const isOpenPopover = ref(false);

  const title = computed(() => t(`sorting.${categoryStore.currentSorting}`));

  const updateHeaderStyles = () => {
    if (isOpenPopover.value && isMobile.value) {
      applyModalStyles();
    } else if (!isOpenPopover.value) {
      resetModalStyles();
    }
  };

  const togglePopoverHandler = () => {
    isOpenPopover.value = !isOpenPopover.value;
    updateHeaderStyles();
  };

  const closePopover = () => {
    isOpenPopover.value = false;
    updateHeaderStyles();
  };

  const chooseSortingValue = (item: ListSortItemValue) => {
    closePopover();

    categoryStore.setSorting(item);
  };

  const isChosenSortValue = (item: string) => item === categoryStore.currentSorting;

  const defaultListSort = useSortList();

  const listSort = computed(() => {
    return defaultListSort.map((item) => {
      return {
        ...item,
        selected: item.value === categoryStore.currentSorting,
      };
    });
  });
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography';
  @use '@experience/shared/dist/assets/adaptivity';

  .sorting {
    position: relative;
    z-index: 9;

    :deep(.popover) {
      right: 0;
      left: auto;
      overflow: hidden;
      padding: 0;
      border-radius: 8px;
    }
  }

  .sorting-modal {
    margin-top: -16px;
  }

  .sorting__title {
    display: flex;
    align-items: center;
    cursor: pointer;
    column-gap: 4px;

    @include typography.base-400-400-medium;

    @include adaptivity.tablet {
      @include typography.base-300-300-medium;
    }
  }

  .sorting__list {
    width: 200px;
    padding: 4px 0;
  }

  .sorting-desktop__list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      background: var(--comp-dropdown-menu-item-color-background-hovered);
    }

    @include typography.base-400-400-regular;
  }

  :deep(.sorting-element) {
    .radio {
      padding: 12px 0;
    }
  }
</style>

<style lang="scss">
  .ui-modal.sorting-modal {
    .ui-modal__body-content-wrapper {
      padding-top: 0;
    }

    .ui-modal-header__content {
      align-items: center;
    }
  }
</style>
